import React, { useState, useEffect } from 'react';
import BaseModal from '../../../../components/modals/BaseModal';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { CloudFunctions } from '../../../../utils/enums/CloudFunctions';
import { Grid, TextField, Button, InputAdornment, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography } from '@mui/material';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { STRIPE_TAX_CODES } from '../../../../utils/stripe/StripeTaxCodes';

const EditProductModal = ({ show, onHide, product }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [isPosItem, setIsPosItem] = useState(false);
    const [isInvoiceItem, setIsInvoiceItem] = useState(false);
    const [taxBehavior, setTaxBehavior] = useState('exclusive');
    const [taxCode, setTaxCode] = useState('txcd_99999999');
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const { organizationStripeInfo } = useOrganizationContext();

    useEffect(() => {
        if (product) {
            setName(product.name);
            setDescription(product.description);
            setPrice((product.default_price.unit_amount / 100).toFixed(2));
            setIsPosItem(product.pos_item);
            setIsInvoiceItem(product.invoice_item);
            setTaxBehavior(product.tax_behavior || 'exclusive');
            setTaxCode(product.tax_code || 'txcd_99999999');
        }
    }, [product]);

    // Reset tax code when tax behavior changes to 'inclusive'
    useEffect(() => {
        if (taxBehavior === 'inclusive') {
            setTaxCode('txcd_99999999'); // Reset to default when inclusive is selected
        }
    }, [taxBehavior]);

    const handleSubmit = async () => {
        setLoadingData(true);
        const functions = getFunctions();
        const updateProduct = httpsCallable(functions, CloudFunctions.UpdateStripeProduct);
        try {
            const result = await updateProduct({
                productId: product.id,
                name,
                description,
                accountId: organizationStripeInfo.stripe_account_id,
                unit_amount: parseInt(price, 10) * 100,
                currency: 'usd',
                pos_item: isPosItem,
                invoice_item: isInvoiceItem,
                tax_behavior: taxBehavior,
                tax_code: taxCode
            });

            if (result.data.success) {
                showToast(t(StringKeys.SUCCESSFULLY_UPDATED_X, { x: `${name}` }), ToastType.SUCCESS);
                onHide();
            } else {
                showToast(t(StringKeys.FAILED_TO_UPDATE_X, { x: `${name}` }), ToastType.ERROR);
                console.error('Error updating product:', result.data.error);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_UPDATE_X, { x: `${name}` }), ToastType.ERROR);
            console.error('Error updating product:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const resetForm = () => {
        if (product) {
            setName(product.name);
            setDescription(product.description);
            setPrice((product.default_price.unit_amount / 100).toFixed(2));
            setIsPosItem(product.pos_item);
            setIsInvoiceItem(product.invoice_item);
            setTaxBehavior(product.tax_behavior || 'exclusive');
            setTaxCode(product.tax_code || 'txcd_99999999');
        }
    };

    // Group tax codes by category for display
    const renderTaxCodeItems = () => {
        // Group by category
        const categories = {};
        STRIPE_TAX_CODES.forEach(code => {
            if (!categories[code.category]) {
                categories[code.category] = [];
            }
            categories[code.category].push(code);
        });

        // Create menu items grouped by category with headers
        return Object.entries(categories).map(([category, codes]) => [
            <MenuItem 
                key={category} 
                disabled 
                divider 
                sx={{ 
                    opacity: 1, 
                    fontWeight: 'bold', 
                    bgcolor: 'action.hover',
                    pointerEvents: 'none'
                }}
            >
                {category}
            </MenuItem>,
            ...codes.map(code => (
                <MenuItem key={code.code} value={code.code} sx={{ pl: 4 }}>
                    {code.name}
                </MenuItem>
            ))
        ]).flat();
    };

    const actions = (
        <>
            <Button variant="contained" color="primary" onClick={handleSubmit} style={{ marginRight: '10px' }}>
                {t(StringKeys.SUBMIT)}
            </Button>
            <Button onClick={onHide} style={{ marginRight: '10px' }}>
                {t(StringKeys.CANCEL)}
            </Button>
        </>
    );

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.EDIT_X, { x: `${product.name}` })}
            actions={actions}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={t(StringKeys.NAME)}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label={t(StringKeys.DESCRIPTION)}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label={t(StringKeys.PRICE)}
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 0, step: 0.01 }
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t(StringKeys.TAX_SETTINGS)}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>{t(StringKeys.INCLUDE_TAX)}</InputLabel>
                        <Select
                            value={taxBehavior}
                            label={t(StringKeys.INCLUDE_TAX)}
                            onChange={(e) => setTaxBehavior(e.target.value)}
                        >
                            <MenuItem value="inclusive">
                                {t(StringKeys.YES)} ({t(StringKeys.TAX_INCLUDED)})
                            </MenuItem>
                            <MenuItem value="exclusive">
                                {t(StringKeys.NO)} ({t(StringKeys.TAX_ADDED)})
                            </MenuItem>
                        </Select>
                        <FormHelperText>
                            {taxBehavior === 'inclusive' 
                                ? t(StringKeys.TAX_FINAL_PRICE_INCLUDES) 
                                : t(StringKeys.TAX_ADDED_TO_PRICE)}
                        </FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" disabled={taxBehavior === 'inclusive'}>
                        <InputLabel>{t(StringKeys.TAX_CODE)}</InputLabel>
                        <Select
                            value={taxCode}
                            label={t(StringKeys.TAX_CODE)}
                            onChange={(e) => setTaxCode(e.target.value)}
                            disabled={taxBehavior === 'inclusive'}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400,
                                        width: 'auto',
                                    },
                                },
                            }}
                        >
                            {renderTaxCodeItems()}
                        </Select>
                        <FormHelperText>
                            {taxBehavior === 'inclusive' 
                                ? t(StringKeys.TAX_CODE_DISABLED_WITH_INCLUSIVE)
                                : t(StringKeys.TAX_DETERMINES_HOW_TAXED)}
                        </FormHelperText>
                    </FormControl>
                </Grid>
                {/* <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isPosItem}
                                onChange={(e) => setIsPosItem(e.target.checked)}
                            />
                        }
                        label={t(StringKeys.POINT_OF_SALE_PRODUCT)}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isInvoiceItem}
                                onChange={(e) => setIsInvoiceItem(e.target.checked)}
                            />
                        }
                        label={t(StringKeys.INVOICE_PRODUCT)}
                    />
                </Grid> */}
            </Grid>

        </BaseModal>
    );
};

export default EditProductModal;
